import React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

/**
 * Centralized button theme + reusable button components so every screen uses
 * the same colors, shapes, and delete control. Import these instead of styling
 * raw MUI <Button>/<IconButton> inline.
 */
export const BUTTON_COLORS = {
  primary: '#8A00E5',
  primaryHover: '#6A0080',
  primaryDisabledBg: 'rgba(138, 0, 229, 0.5)',
  danger: '#DA1E28',
  dangerHover: '#B01B22',
  dangerSoftHover: 'rgba(218, 30, 40, 0.08)',
  primarySoftHover: 'rgba(138, 0, 229, 0.08)',
  neutralText: '#374151',
  neutralBorder: '#d1d5db',
} as const;

const mergeSx = (base: object, override?: ButtonProps['sx']) =>
  (Array.isArray(override) ? [base, ...override] : [base, override]) as ButtonProps['sx'];

/** Solid purple button for primary actions (Save, Add, Import, Confirm). */
export const PrimaryButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function PrimaryButton({ sx, variant = 'contained', ...props }, ref) {
    return (
      <Button
        ref={ref}
        variant={variant}
        {...props}
        sx={mergeSx(
          {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1,
            boxShadow: 'none',
            bgcolor: BUTTON_COLORS.primary,
            color: '#fff',
            '&:hover': { bgcolor: BUTTON_COLORS.primaryHover, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: BUTTON_COLORS.primaryDisabledBg, color: '#fff' },
          },
          sx
        )}
      />
    );
  }
);

/** Outlined purple button for secondary actions (Edit, Cancel-ish, New, Add row). */
export const SecondaryButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function SecondaryButton({ sx, variant = 'outlined', ...props }, ref) {
    return (
      <Button
        ref={ref}
        variant={variant}
        {...props}
        sx={mergeSx(
          {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1,
            color: BUTTON_COLORS.primary,
            borderColor: BUTTON_COLORS.primary,
            bgcolor: '#fff',
            '&:hover': {
              borderColor: BUTTON_COLORS.primaryHover,
              color: BUTTON_COLORS.primaryHover,
              background: BUTTON_COLORS.primarySoftHover,
            },
          },
          sx
        )}
      />
    );
  }
);

/** Solid red button for destructive confirmations (Delete, Yes/Remove in dialogs). */
export const DangerButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function DangerButton({ sx, variant = 'contained', ...props }, ref) {
    return (
      <Button
        ref={ref}
        variant={variant}
        {...props}
        sx={mergeSx(
          {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1,
            boxShadow: 'none',
            bgcolor: BUTTON_COLORS.danger,
            color: '#fff',
            '&:hover': { bgcolor: BUTTON_COLORS.dangerHover, boxShadow: 'none' },
          },
          sx
        )}
      />
    );
  }
);

/** Outlined red button (e.g. the top-level Delete action before confirmation). */
export const DangerOutlineButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function DangerOutlineButton({ sx, variant = 'outlined', ...props }, ref) {
    return (
      <Button
        ref={ref}
        variant={variant}
        {...props}
        sx={mergeSx(
          {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1,
            color: BUTTON_COLORS.danger,
            borderColor: BUTTON_COLORS.danger,
            bgcolor: '#fff',
            '&:hover': {
              borderColor: BUTTON_COLORS.dangerHover,
              color: BUTTON_COLORS.dangerHover,
              background: BUTTON_COLORS.dangerSoftHover,
            },
          },
          sx
        )}
      />
    );
  }
);

type DeleteIconButtonProps = Omit<IconButtonProps, 'color'> & {
  /** Tooltip / aria label for the control. Defaults to "Delete". */
  label?: string;
};

/**
 * The single, uniform delete control used everywhere (table rows, list items,
 * chips). Always a red trash icon with consistent sizing.
 */
export const DeleteIconButton = React.forwardRef<HTMLButtonElement, DeleteIconButtonProps>(
  function DeleteIconButton({ sx, label = 'Delete', size = 'small', ...props }, ref) {
    return (
      <Tooltip title={label}>
        <span>
          <IconButton
            ref={ref}
            size={size}
            aria-label={label}
            {...props}
            sx={mergeSx(
              {
                p: 0.5,
                color: BUTTON_COLORS.danger,
                '&:hover': { color: BUTTON_COLORS.dangerHover, bgcolor: BUTTON_COLORS.dangerSoftHover },
              },
              sx as IconButtonProps['sx']
            )}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }
);

type EditIconButtonProps = Omit<IconButtonProps, 'color'> & {
  label?: string;
};

/** Uniform edit control — purple pencil icon, same size as DeleteIconButton. */
export const EditIconButton = React.forwardRef<HTMLButtonElement, EditIconButtonProps>(
  function EditIconButton({ sx, label = 'Edit', size = 'small', ...props }, ref) {
    return (
      <Tooltip title={label}>
        <span>
          <IconButton
            ref={ref}
            size={size}
            aria-label={label}
            {...props}
            sx={mergeSx(
              {
                p: 0.5,
                color: BUTTON_COLORS.primary,
                '&:hover': { color: BUTTON_COLORS.primaryHover, bgcolor: BUTTON_COLORS.primarySoftHover },
              },
              sx as IconButtonProps['sx']
            )}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }
);
